import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {LaColorService} from '../colors/la-color.service';

/**
 * @group Buttons
 * @component Button
 * @description some description
 */
@Component({
  selector: 'la-button',
  template: `
    <button #button type="button" class="la-button" (click)="onClick()" [ngClass]="{
        'la-button--wide': isWide,
        'la-button--primary': kind === 'primary',
        'la-button--secondary': kind === 'secondary',
        'la-button--tertiary': kind === 'tertiary',
        'la-button--default': kind === 'default',
        'la-button--text': kind === 'text',
        'la-button--small': size === 'sm',
        'la-button--x-small': size === 'xsm',
        'la-button--hover': isHover,
        'la-button--disabled': isDisabled}"
            [ngStyle]="{'background': bgColor}"
            [style.color]="textColor"
            [style.border-color]="borderColor"
            [style.box-shadow]="boxShadow"
            [disabled]="isDisabled">
      <la-text [kind]="'button'">
        <div class="la-button__text">
          <ng-content></ng-content>
        </div>
      </la-text>
    </button>
  `,
  styles: [`
    @font-face{font-family:NeueHaasGrotDisp-45Light;font-style:normal;font-weight:300;src:url(assets/fonts/NeueHaasGrotDisp-45Light.woff) format("woff")}@font-face{font-family:NeueHaasGrotDisp-55Roman;font-style:normal;font-weight:400;src:url(assets/fonts/NeueHaasGrotDisp-55Roman.woff) format("woff")}@font-face{font-family:NeueHaasUnicaPro;font-style:normal;font-weight:400;src:url(assets/fonts/NeueHaasUnicaPro-Regular.woff) format("woff")}@font-face{font-family:NeueHaasUnicaPro;font-style:normal;font-weight:500;src:url(assets/fonts/NeueHaasUnicaPro-Medium.woff) format("woff")}.la-button{font-size:17px;font-family:NeueHaasUnicaPro,sans-serif;font-weight:500;text-align:center;letter-spacing:-.7px;line-height:20.4px;border-radius:8px;height:54px;border:1px solid #23be9e;color:rgba(68,72,75,.82);background-color:#fff;padding:15px 32px 18px;cursor:pointer}.la-button--hover,.la-button:hover{opacity:.6}.la-button.la-button--disabled{color:#fff;background:none;border:1px solid rgba(3,20,34,.15);cursor:not-allowed}.la-button.la-button--disabled .la-text>.la-button__text>la-icon>svg{fill:#fff!important}.la-button.la-button--disabled .la-text>.la-button__text{color:#fff}.la-button--primary{color:#fff;background:#23be9e;border:none}.la-button--primary.la-button--disabled{background:rgba(3,20,34,.15);border:none;color:#fff;cursor:not-allowed}.la-button--secondary{color:#23be9e;background:rgba(35,190,158,.15);border:none}.la-button--secondary.la-button--disabled{background:rgba(3,20,34,.15);color:#fff;border:none;cursor:not-allowed}.la-button--tertiary{color:#23be9e;background:#fff;border:1px solid #23be9e}.la-button--tertiary.la-button--disabled{background:none;border:1px solid rgba(3,20,34,.15);cursor:not-allowed}.la-button--tertiary.la-button--disabled .la-text>.la-button__text>la-icon>svg{fill:rgba(3,20,34,.15)!important}.la-button--tertiary.la-button--disabled .la-text>.la-button__text{color:rgba(3,20,34,.15)}.la-button--text{color:#23be9e;background:transparent;border:none}.la-button--text.la-button--disabled{background:none;color:#fff;border:none;cursor:not-allowed}.la-button--default{color:rgba(68,72,75,.82);background:rgba(127,133,141,.07);border:none}.la-button--default.la-button--disabled{background:rgba(3,20,34,.15);border:none;color:#031422;cursor:not-allowed}.la-button--small{height:40px;padding:6px 20px 10px}.la-button--x-small{height:32px;padding:1px 12px 6px}.la-button--x-small .la-text .la-text--button{font-size:15px}.la-button .la-button__text{display:flex;justify-content:center;align-items:center}.la-button .la-button__text la-icon:first-child{margin-right:4px}.la-button .la-button__text la-icon:nth-child(2){margin-left:4px}button:focus{outline:none}.la-button--wide{width:100%}
  `],
  encapsulation: ViewEncapsulation.None
})
export class LaButtonComponent implements AfterViewInit {
  private _isWide = false;
  private _kind = ''; // primary, secondary
  private _isDisabled = false;
  private _isHover = false;
  private _size = ''; // sm xsm
  @Output() clicked: EventEmitter<any> = new EventEmitter();
  private _colorName: string; // color name
  public bgColor: string; // HEX or rgba CSS color
  public borderColor: string; // HEX or rgba CSS color
  public textColor: string; // HEX or rgba CSS color
  public fillColor: string; // HEX or rgba CSS color
  public boxShadow: string; // HEX or rgba CSS color
  @ViewChild('button') _el: ElementRef;

  constructor(private _laColorService: LaColorService) {
  }

  ngAfterViewInit(): void {
    if (this.fillColor && this._el.nativeElement.getElementsByTagName('svg')[0]) {
      this._el.nativeElement.getElementsByTagName('svg')[0].style.fill = this.fillColor;
    }
  }

  onClick() {
    if (!this.isDisabled) {
      this.clicked.next();
    }
  }

  get isWide(): boolean {
    return this._isWide;
  }

  /**
   * true - to make button wide. false - by default
   */
  @Input()
  set isWide(isWide: boolean) {
    this._isWide = isWide;
  }

  get kind(): string {
    return this._kind;
  }

  /**
   * Kind of a button: primary, secondary, default.
   */
  @Input()
  set kind(style: string) {
    this._kind = style;
  }

  // [isDisabled]
  get isDisabled(): boolean {
    return this._isDisabled;
  }

  /**
   * true - to make button disabled. false - by default
   */
  @Input()
  set isDisabled(value: boolean) {
    this._isDisabled = value;
  }

  get isHover(): boolean {
    return this._isHover;
  }

  /**
   * To apply hovered style to a button. only used in style guide
   */
  @Input()
  set isHover(value: boolean) {
    this._isHover = value;
  }

  /**
   * sm - to make button size small. xsm - to make button extra small amd large by default
   */
  @Input()
  set size(size: string) {
    this._size = size;
  }

  get size(): string {
    return this._size;
  }

  get color(): string {
    return this._colorName;
  }

  /**
   * To apply color to button bg color. support only color palates color.
   */
  @Input()
  set color(colorName: string) {
    this._colorName = colorName.toLowerCase();
    const color = this._laColorService.getColorByName(colorName);
    if (this.kind === 'primary') {
      this.bgColor = color;
    }

    if (this.kind === 'tertiary') {
      this.borderColor = color;
      this.textColor = color;
    }

    if (this.kind === 'text') {
      this.fillColor = this.textColor = color;
    }

    if (this.kind === 'secondary') {
      this.bgColor = this._laColorService.hexToRgb(color, 0.1);
      this.textColor = color;
    }

    if (this.kind === 'default') {
      this.textColor = color;
    }
  }

}
